import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Award,
  CalendarCheck,
  Download,
  KeyRound,
  Loader2,
  MapPin,
  Monitor,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import type { Participant, Percurso, PercursoStep, PercursoStepKind } from 'shared';
import { useParticipant, useParticipantPercurso } from '@/hooks/useParticipants';
import { useDataExport } from '@/hooks/useDataExport';
import { ErrorBlock, LoadingBlock, UniversoChip } from '@/components/activities/ActivityUi';
import DeleteParticipantModal from '@/components/DeleteParticipantModal';
import ResetLinkDialog from '@/components/ResetLinkDialog';
import VisitsSection from '@/components/visits/VisitsSection';
import ParticipantStatusBadge from '@/components/ParticipantStatusBadge';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';
import {
  STEP_KIND_LABEL,
  formatHours,
  formatStepTime,
  formatStopDay,
  monthsNewestFirst,
} from '@/lib/percursoView';

const STEP_ICON: Record<PercursoStepKind, LucideIcon> = {
  visit: MapPin,
  session: CalendarCheck,
  badge: Award,
  equipment: Monitor,
};

const STEP_TONE: Record<PercursoStepKind, string> = {
  visit: 'bg-muted text-muted-foreground',
  session: 'bg-primary/10 text-primary',
  badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  equipment: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card px-4 py-3">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">{label}</p>
      <p className="text-2xl font-bold text-foreground tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

function StepRow({ step }: { step: PercursoStep }) {
  const Icon = STEP_ICON[step.kind];
  return (
    <li className="flex items-start gap-3 py-1.5">
      <span
        className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${STEP_TONE[step.kind]}`}
        title={STEP_KIND_LABEL[step.kind]}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground flex items-center gap-2 flex-wrap">
          <span className="sr-only">{STEP_KIND_LABEL[step.kind]}: </span>
          <span className="font-medium">{step.title}</span>
          {step.universo && <UniversoChip universo={step.universo} />}
        </p>
        <p className="text-xs text-muted-foreground">
          {[step.kind === 'visit' ? null : step.place, formatStepTime(step)]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </li>
  );
}

function PercursoSection({ percurso, name }: { percurso: Percurso; name: string }) {
  const { totals } = percurso;
  if (percurso.stops.length === 0) {
    return (
      <div className="card py-16 text-center space-y-1">
        <p className="text-base font-medium text-muted-foreground">O percurso de {name} ainda está vazio.</p>
        <p className="text-sm text-muted-foreground">
          Começa na primeira entrada num centro, sessão ou badge.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat label="Dias" value={String(totals.days)} />
        <Stat label="Visitas" value={String(totals.visits)} />
        <Stat label="Sessões" value={String(totals.sessions)} />
        <Stat label="Badges" value={String(totals.badges)} />
        <Stat label="Equipamento" value={formatHours(totals.equipmentHours)} />
      </div>

      {totals.universos.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground">
          <span>Universos:</span>
          {totals.universos.map((u) => (
            <span key={u.universo} className="inline-flex items-center gap-1.5">
              <UniversoChip universo={u.universo} />
              <span className="tabular-nums">
                {u.sessions} {u.sessions === 1 ? 'sessão' : 'sessões'}
                {u.badges > 0 && ` · ${u.badges} ${u.badges === 1 ? 'badge' : 'badges'}`}
              </span>
            </span>
          ))}
        </div>
      )}

      {monthsNewestFirst(percurso.stops).map((month) => (
        <section key={month.key} aria-labelledby={`mes-${month.key}`} className="space-y-2">
          <h3 id={`mes-${month.key}`} className="text-sm font-semibold text-foreground">
            {month.label}
          </h3>
          <ol className="card divide-y divide-border">
            {month.stops.map((stop) => (
              <li key={stop.day} className="grid grid-cols-[8rem_1fr] gap-4 px-4 py-3">
                <p className="text-sm font-medium text-foreground pt-2 capitalize">
                  {formatStopDay(stop.day)}
                </p>
                <ul>
                  {stop.steps.map((step, i) => (
                    <StepRow key={`${step.kind}-${step.at}-${i}`} step={step} />
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function Header({
  participant,
  onExport,
  exporting,
  onDelete,
  onResetLink,
}: {
  participant: Participant;
  onExport: () => void;
  exporting: boolean;
  onDelete: () => void;
  onResetLink: () => void;
}) {
  const born = participant.dataNascimento.slice(0, 4);
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-foreground">
            {participant.nome} {participant.apelido}
          </h1>
          <ParticipantStatusBadge status={participant.estadoRegisto} />
        </div>
        <p className="text-muted-foreground mt-1">
          {[
            `N.º ${participant.id}`,
            `Ano de nascimento ${born}`,
            participant.digitalCenterName ? `Centro habitual: ${participant.digitalCenterName}` : 'Sem centro habitual',
          ].join(' · ')}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onResetLink}
          title="Uma ligação para definir uma nova palavra-passe, para entregares à pessoa"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
        >
          <KeyRound className="w-4 h-4" />
          Recuperar palavra-passe
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={exporting}
          title="Tudo o que a app guarda sobre esta pessoa, num ficheiro, para um pedido de acesso (RGPD)"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Exportar dados
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Eliminar
        </button>
      </div>
    </div>
  );
}

/**
 * One participant: their visits to correct, their percurso, and the
 * data-protection actions on them.
 */
export default function ParticipantePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toasts, toast, dismiss } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const participantId = Number(id);
  const validId = Number.isInteger(participantId) && participantId > 0;
  const participantQuery = useParticipant(validId ? participantId : null);
  const percursoQuery = useParticipantPercurso(validId ? participantId : null);
  const dataExport = useDataExport();

  const participant = participantQuery.data;
  const name = participant ? `${participant.nome} ${participant.apelido}` : '';

  const handleExport = () => {
    dataExport.mutate(
      { subject: 'participant', id: participantId },
      {
        onSuccess: () => toast.success(`Dados de ${name} exportados. O ficheiro está nas transferências.`),
        onError: () => toast.error('Não foi possível exportar os dados.'),
      },
    );
  };

  return (
    <>
      <Helmet>
        <title>{participant ? `${name} | DIGGER` : 'Participante | DIGGER'}</title>
      </Helmet>

      <div className="space-y-6">
        <Link
          to="/users"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Utilizadores
        </Link>

        {!validId && <ErrorBlock message="Participante inválido." />}
        {validId && participantQuery.isLoading && <LoadingBlock />}
        {validId && participantQuery.isError && <ErrorBlock message="Não foi possível carregar o participante." />}

        {participant && (
          <>
            <Header
              participant={participant}
              onExport={handleExport}
              exporting={dataExport.isPending}
              onDelete={() => setDeleting(true)}
              onResetLink={() => setResettingPassword(true)}
            />

            <VisitsSection
              participantId={participant.id}
              participantName={name}
              defaultCenterId={participant.digitalCenterId ?? null}
              onSuccess={toast.success}
              onError={toast.error}
            />

            <section aria-labelledby="percurso-heading" className="space-y-3">
              <h2 id="percurso-heading" className="text-lg font-semibold text-foreground">
                Percurso
              </h2>
              {percursoQuery.isLoading && <LoadingBlock />}
              {percursoQuery.isError && <ErrorBlock message="Não foi possível carregar o percurso." />}
              {percursoQuery.data && <PercursoSection percurso={percursoQuery.data} name={participant.nome} />}
            </section>
          </>
        )}
      </div>

      {participant && deleting && (
        <DeleteParticipantModal
          participant={participant}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate('/users', { replace: true })}
        />
      )}
      {participant && resettingPassword && (
        <ResetLinkDialog
          kind="participant"
          id={participant.id}
          name={name}
          onClose={() => setResettingPassword(false)}
        />
      )}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
